import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";
import { useGetCoursesQuery } from "@/queries/useGetCoursesQuery";
import { getCourses } from "@/http/coursesApi";
import { getCoursesQueryKey } from "@/utils/queryKeys";
import { getCookie } from "@/utils/cookies";
import { CalendarDays, ChevronDown, CalendarCheck, Users } from "lucide-react";
import { SIGN_UP, SIGN_UP_GROUPS } from "../utils/const";
import SignUpSlot, { SignUpSlotStrSkeleton } from "../components/SignUpSlot";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import SignUpDate from "../components/SignUpDate";
import CoursesList from "../components/CoursesList";
import s from "../styles/modules/SignUp.module.scss";
import { cn } from "../lib/utils";
import BreadcrumbComponent from "../components/BreadcrumbComponent";
import { useSignUp } from "../hooks/useSignUp";
import { useClub } from "@/contexts/ClubContext";

const SignUp = () => {
  const {
    selectedClubValue,
    openAccordion,
    slots,
    slotDays,
    listOfTrainers,
    setOpenAccordion,
    formatDate,
    formatDayOfWeek,
    handleClubChange,
    clubsArr,
    selectedTrainer,
    setSelectedTrainer,
    slotsLoading,
    appointmentsData,
    userName,
    hasBookingOnDay,
    getBookingClubOnDay,
  } = useSignUp();

  const { selectedClubId } = useClub();
  const location = useLocation();
  const navigate = useNavigate();

  // Показываем вкладку "Групповые" только если в выбранном филиале реально есть секции.
  // Если юзер на /sign-up/groups, а клуб групп не имеет — URL не трогаем, просто
  // показываем плашку внутри контента. Так он поймёт что произошло и сможет сменить
  // филиал обратно. Временная ошибка сети тоже не перекинет его на индивидуалки.
  const { data: clubCourses = [] } = useGetCoursesQuery(selectedClubId);
  const hasGroups = clubCourses.length > 0;

  // Тянем курсы для всех клубов параллельно, чтобы в селекте филиалов отметить те,
  // где доступны только персоналки. Кэш разделяется с useGetCoursesQuery — без дублей.
  const userToken = getCookie("user_token");
  const allClubsCourses = useQueries({
    queries: clubsArr.map((club) => ({
      queryKey: [...getCoursesQueryKey(club.id), { userToken }],
      queryFn: async () => {
        const response = await getCourses(club.id, userToken || undefined);
        return response.data.data ?? [];
      },
      staleTime: 5 * 60_000,
    })),
  });
  const clubHasGroups = useMemo(() => {
    const map = new Map<string, boolean>();
    clubsArr.forEach((club, i) => {
      const data = allClubsCourses[i]?.data;
      // Пока запрос не завершился — не показываем подпись (data === undefined).
      if (data !== undefined) map.set(club.id, data.length > 0);
    });
    return map;
  }, [clubsArr, allClubsCourses]);

  const activeTab = location.pathname === SIGN_UP_GROUPS ? "courses" : "individual";
  const handleTabChange = (value: string) => {
    navigate(value === "courses" ? SIGN_UP_GROUPS : SIGN_UP);
  };

  const slotDaysWithSlots = useMemo(
    () =>
      slotDays.reduce(
        (acc, date) => {
          const slotsForDate = slots.filter(
            (slot) =>
              slot.date_time.startsWith(date) &&
              (selectedTrainer === "all" ||
                !selectedTrainer ||
                slot.employee_id === selectedTrainer)
          );
          if (slotsForDate.length > 0) {
            acc.push({ date, slots: slotsForDate });
          }
          return acc;
        },
        [] as Array<{ date: string; slots: typeof slots }>
      ),
    [slots, slotDays, selectedTrainer]
  );

  return (
    <div className={s.wrapper_sign_up}>
      <div className="rounded-lg bg-white p-3">
        <BreadcrumbComponent link={"Расписание"} />
        <div className="mb-6 flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-medium text-gray-900">Расписание</h2>
        </div>

        <div className="mb-6 grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-3 sm:flex sm:flex-wrap sm:gap-4">
          <span className="text-sm font-medium text-gray-700">Филиал:</span>
          <Select
            value={selectedClubValue}
            onValueChange={(value) => {
              const selectedClub = clubsArr.find((club) => club.title === value);
              if (selectedClub) {
                handleClubChange(selectedClub.id);
                navigate(SIGN_UP);
              }
            }}
          >
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="Выберите филиал" />
            </SelectTrigger>
            <SelectContent>
              {clubsArr.map(({ title, id }) => (
                <SelectItem key={id} value={title}>
                  {title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <span className="text-sm font-medium text-gray-700">Тренер:</span>
          <Select value={selectedTrainer} onValueChange={setSelectedTrainer}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="Все тренеры" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все тренеры</SelectItem>
              {listOfTrainers.map((trainer) => (
                <SelectItem key={trainer.id} value={trainer.id}>
                  {trainer.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedClubId && clubHasGroups.get(selectedClubId) === false && (
          <p className="mb-3 text-sm text-muted-foreground">
            В этом филиале доступны только персональные занятия
          </p>
        )}

        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          {hasGroups && (
            <TabsList className="mb-4 grid h-auto w-full grid-cols-2 py-1.5">
              <TabsTrigger
                value="individual"
                className="flex h-auto flex-col items-center justify-center gap-1.5 whitespace-normal px-2 py-2.5 text-center text-sm leading-tight"
              >
                <CalendarDays className="h-5 w-5 shrink-0" />
                <span>
                  Персональные
                  <br />
                  занятия
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="courses"
                className="flex h-auto flex-col items-center justify-center gap-1.5 whitespace-normal px-2 py-2.5 text-center text-sm leading-tight"
              >
                <Users className="h-5 w-5 shrink-0" />
                <span>
                  Групповые
                  <br />
                  занятия
                </span>
              </TabsTrigger>
            </TabsList>
          )}

          <TabsContent value="individual" className="mt-0">
            <div className="space-y-3">
              {slotsLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="rounded-lg border bg-muted/50 p-4">
                      <div className="space-y-3">
                        <SignUpSlotStrSkeleton />
                        <SignUpSlotStrSkeleton />
                      </div>
                    </div>
                  ))}
                </div>
              ) : slotDaysWithSlots.length > 0 ? (
                slotDaysWithSlots.map(({ date, slots: slotsByDate }) => (
                  <Accordion
                    type="single"
                    collapsible
                    key={date}
                    value={openAccordion}
                    onValueChange={setOpenAccordion}
                  >
                    <AccordionItem
                      value={date}
                      className="group relative w-full overflow-hidden rounded-lg border bg-white"
                    >
                      <AccordionTrigger
                        className={cn(
                          "w-full px-4 py-3 transition-all duration-200 hover:no-underline sm:px-6 sm:py-4",
                          "data-[state=open]:bg-muted/30",
                          "[&[data-state=open]>div]:text-primary [&[data-state=open]_.icon]:text-primary",
                          "[&[data-state=open]_.chevron]:rotate-180",
                          "group hover:bg-muted/30",
                          "bg-muted/50 [&>svg]:hidden"
                        )}
                      >
                        <div
                          className={`flex w-full items-center gap-2 transition-colors duration-200 sm:gap-3 ${s.date_of_sign_up} `}
                        >
                          <div className="flex items-center gap-2">
                            <div className="rounded-full bg-primary/10 p-2 sm:p-2">
                              <CalendarDays className="icon h-5 w-5 transition-colors duration-200" />
                            </div>
                            <div className="flex min-w-[92px] flex-col items-start">
                              <span className="font-medium">{formatDate(date)}</span>
                              <span className="text-sm capitalize text-gray-500">
                                {formatDayOfWeek(date)}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center">
                            <SignUpDate date={date} trainers={listOfTrainers} slots={slots} />
                          </div>
                          <div className="ml-auto flex items-center gap-2">
                            {hasBookingOnDay(date) && (
                              <Badge variant="secondary" className="gap-1.5">
                                <CalendarCheck className="h-3 w-3" />
                                <span className="hidden sm:inline">
                                  Есть запись
                                  {getBookingClubOnDay(date, selectedClubValue) &&
                                    ` (${getBookingClubOnDay(date, selectedClubValue)})`}
                                </span>
                              </Badge>
                            )}
                            <span
                              className={`text-sm text-gray-500 transition-colors duration-200 group-hover:text-gray-900 ${s.text_show_more_title}`}
                            >
                              Показать занятия
                            </span>
                            <div
                              className={`rounded-full bg-gray-50 p-2 transition-colors duration-200 group-hover:bg-gray-100 ${s.icon_show_more_title}`}
                            >
                              <ChevronDown className="chevron ease-&lsqb;cubic-bezier(0.87,_0,_0.13,_1)&rsqb; h-4 w-4 text-gray-500 transition-all duration-300 group-hover:text-gray-900" />
                            </div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                        <div className="px-6 pb-4 pt-2">
                          <div className="space-y-3 border-t pt-4">
                            {slotsByDate.map((slot, index) => (
                              <SignUpSlot
                                key={index}
                                trainers={listOfTrainers}
                                slot={slot}
                                appointmentsData={appointmentsData}
                                userName={userName}
                                onSuccessfulSignUp={() => {}}
                              />
                            ))}
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                ))
              ) : (
                <div className="px-4 py-8 text-center">
                  <CalendarDays className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                  <h3 className="mb-1 text-lg font-medium text-gray-900">Нет доступных занятий</h3>
                  <p className="text-sm text-gray-500">
                    Пожалуйста, выберите другой филиал или попробуйте позже
                  </p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="courses" className="mt-0">
            {hasGroups ? (
              <CoursesList clubId={selectedClubId || null} trainerId={selectedTrainer} />
            ) : (
              <div className="px-4 py-12 text-center">
                <Users className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                <h3 className="mb-1 text-lg font-medium text-gray-900">
                  В этом филиале нет групповых занятий
                </h3>
                <p className="text-sm text-gray-500">
                  Выберите другой филиал или перейдите на индивидуальные занятия
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SignUp;
